import {
  DependencyData,
  ResolvedDependency,
} from '@wix/ambassador-app-service-webapp/http';
import type { MarketListing } from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/types';
import { DocumentServicesObject } from '@wix/document-services-types';
import {
  AppInstallOrigin,
  DependenciesDataDriver,
  InstallerAppsData,
} from '@wix/editor-platform-host-integration-apis/';
import { InstallInitiator } from '@wix/editor-platform-sdk-types/';
import { appMarketService } from '../utils/appMarketService';
import { isGuid } from '../utils/guid';
import { getEditorType } from './intentInstallService/helpers';

const filterActiveApp = (
  documentServices: DocumentServicesObject,
  dependenciesApps: ResolvedDependency[],
) =>
  dependenciesApps.filter(
    ({ appId }) => !documentServices.platform.isAppActive(appId!),
  );

const getAppIcon = (appMarketData: MarketListing): string | undefined => {
  return appMarketData?.assetsMap?.screenshots?.assets?.[0]?.url;
};

const buildAppData = (appMarketData: MarketListing) => ({
  label: appMarketData.basicInfo?.name || undefined,
  appIconUrl: getAppIcon(appMarketData),
  info: appMarketData?.basicInfo?.teaser || undefined,
  notify: false,
});

const appDataFallback = {
  label: '',
  appIconUrl: undefined,
  info: undefined,
  notify: false,
};

interface internalAppsMap {
  required: string[];
  optional: string[];
  provisionOnlyChild: Set<string>;
  childToNotify: Set<string>;
  parents: Set<string>;
  data: Omit<InstallerAppsData, 'appDefId'>;
}

const createInitialAppInternalData = (
  data: Omit<InstallerAppsData, 'appDefId'>,
) => ({
  required: [],
  optional: [],
  provisionOnlyChild: new Set<string>(),
  childToNotify: new Set<string>(),
  parents: new Set<string>(),
  data,
});

const updateParentsForRequiredApps = (
  appsMap: Record<string, internalAppsMap>,
  appId: string,
  shouldAdd: boolean,
  acc: string[] = [],
) => {
  appsMap[appId]?.required.forEach((child) => {
    if (shouldAdd) {
      appsMap[child]?.parents.add(appId);
    } else {
      appsMap[child]?.parents.delete(appId);
    }
    updateParentsForRequiredApps(appsMap, child, shouldAdd, acc);
    acc.push(child);
  });
  return acc;
};

const initDriver = (
  appsMap: Record<string, internalAppsMap>,
  appToInstallDefinitionId: string,
) => {
  const optionalApps = appsMap[appToInstallDefinitionId].optional;
  optionalApps.forEach((optionalApp) => {
    appsMap[optionalApp].data.toggle = false;
    appsMap[optionalApp].data.notify = true;
  });
  return buildAppsToInstall(appsMap, appToInstallDefinitionId);
};

const updateAppsNotifyStatus = (
  appsToInstall: string[],
  appsMap: Record<string, internalAppsMap>,
) => {
  appsToInstall.forEach((app) => {
    let shouldNotifyUser = false;
    const appData = appsMap[app];
    if (appData) {
      if (appData.data.toggle === undefined) {
        shouldNotifyUser = Array.from(appData.parents).some((parent) =>
          appsMap[parent].childToNotify.has(app),
        );
        appData.data.notify = shouldNotifyUser;
      }
    }
  });
};

const buildAppsToInstall = (
  appsMap: Record<string, internalAppsMap>,
  appToInstallDefinitionId: string,
) => {
  const appsToInstall = new Set([
    ...updateParentsForRequiredApps(appsMap, appToInstallDefinitionId, true),
  ]);
  const toggledApps = Object.keys(appsMap).filter(
    (optionalApp) => appsMap[optionalApp].data.toggle,
  );
  toggledApps.forEach((app) => {
    const reqApps = updateParentsForRequiredApps(appsMap, app, true);
    reqApps.forEach((reqApp) => appsToInstall.add(reqApp));
    appsToInstall.add(app);
  });
  appsToInstall.add(appToInstallDefinitionId);
  return Array.from(appsToInstall);
};

const initNonGuidApp = (
  appsMap: Record<string, internalAppsMap>,
  appToInstallDefinitionId: string,
) => {
  appsMap[appToInstallDefinitionId] = createInitialAppInternalData({
    notify: false,
  });
  return buildAppsToInstall(appsMap, appToInstallDefinitionId);
};

const createDependenciesDriver = async (
  documentServices: DocumentServicesObject,
  appToInstallDefinitionId: string,
  // Kept for API compatibility; the apps-runtime reader resolves the
  // current runtime data and does not take a version.
  _appVersion?: string,
): Promise<DependenciesDataDriver> => {
  const appsMap: Record<string, internalAppsMap> = {};
  let appsToInstall: string[] = [];
  let appMarketDataAppDefinitionIds: string[] = [];

  if (!isGuid(appToInstallDefinitionId)) {
    // runtime apps special treatment - apps like wix-data
    appsToInstall = initNonGuidApp(appsMap, appToInstallDefinitionId);
  } else {
    // TODO think if we should put in cache
    let nonActiveDependenciesApp: ResolvedDependency[];
    let appsMarketData: Record<string, MarketListing>;
    try {
      nonActiveDependenciesApp = filterActiveApp(
        documentServices,
        await appMarketService.getAppDependencies(appToInstallDefinitionId),
      );
      appMarketDataAppDefinitionIds = [
        appToInstallDefinitionId,
        ...(nonActiveDependenciesApp.map(({ appId }) => appId) as string[]),
      ];
      // TODO think if we should put in cache
      appsMarketData = await appMarketService.getAppsInfoByLanguageWithDefaults(
        {
          appDefinitionIds: appMarketDataAppDefinitionIds,
          languageCode: documentServices.generalInfo.getLanguage(),
        },
      );
    } catch (e) {
      throw new Error(`appMarket call to dependencies api has failed. ${e}`);
    }

    Object.entries(appsMarketData).forEach(([appId, appMarketData]) => {
      appsMap[appId] = createInitialAppInternalData(
        buildAppData(appMarketData),
      );
    });

    // WA for cases when app market listings are missing
    appMarketDataAppDefinitionIds.forEach((appDefId) => {
      if (!appsMap[appDefId]) {
        appsMap[appDefId] = createInitialAppInternalData(appDataFallback);
      }
    });

    nonActiveDependenciesApp.forEach((dependencyApp) => {
      const appId = dependencyApp.appId!;

      dependencyApp.parentRelations?.forEach((parent) => {
        const parentId = parent.appId!;
        // If the parent is already active it was filtered out of appsMap.
        // Re-route the child directly to the root app so it still gets installed.
        const effectiveParentId = appsMap[parentId]
          ? parentId
          : appToInstallDefinitionId;

        const flags = parent.dependencyData ?? [];

        if (flags.includes(DependencyData.IS_BUNDLE)) {
          appsMap[effectiveParentId].optional.push(appId);
        } else {
          appsMap[effectiveParentId].required.push(appId);
        }

        if (flags.includes(DependencyData.NOTIFY_USER_ON_DEPENDENCY)) {
          appsMap[effectiveParentId].childToNotify.add(appId);
        }

        if (flags.includes(DependencyData.PROVISION_ONLY_INSTALLATION)) {
          appsMap[effectiveParentId].provisionOnlyChild.add(appId);
        }
      });
    });
    appsToInstall = initDriver(appsMap, appToInstallDefinitionId);
    updateAppsNotifyStatus(appsToInstall, appsMap);
  }

  const toggleOptionalDependency = (appId: string) => {
    if (appsMap[appId]?.data?.toggle === undefined) {
      throw new Error('it is possible to toggle only optional dependency apps');
    }
    appsMap[appId].data.toggle = !appsMap[appId].data.toggle;
    if (appsMap[appId].data.toggle) {
      appsMap[appId].parents.add(appToInstallDefinitionId);
    } else {
      appsMap[appId].parents.delete(appToInstallDefinitionId);
    }
    updateParentsForRequiredApps(appsMap, appId, appsMap[appId].data.toggle!);
    appsToInstall = buildAppsToInstall(appsMap, appToInstallDefinitionId);
    updateAppsNotifyStatus(appsToInstall, appsMap);
  };

  const getAppsToProvisionHeadless = () => {
    const appsToProvisionHeadless: Record<string, boolean> = {};
    appsToInstall.forEach((app) => {
      const parents = Array.from(appsMap[app]?.parents ?? []);
      appsToProvisionHeadless[app] =
        parents.length > 0 &&
        parents.every((parent) => appsMap[parent].provisionOnlyChild.has(app));
    });
    return appsToProvisionHeadless;
  };

  const getAppsToInstall = () => {
    const appsToProvisionHeadless = getAppsToProvisionHeadless();

    const dependencyOptions: Record<
      string,
      {
        headlessInstallation?: boolean;
        origin: AppInstallOrigin;
      }
    > = {};

    appsToInstall.forEach((app) => {
      if (app !== appToInstallDefinitionId) {
        dependencyOptions[app] = {
          origin: {
            type: getEditorType(),
            initiator: InstallInitiator.Dependency_Service,
            info: {
              appDefinitionId: appToInstallDefinitionId,
            },
          },
        };
      }
      if (appsToProvisionHeadless[app]) {
        dependencyOptions[app] = {
          ...dependencyOptions[app],
          headlessInstallation: appsToProvisionHeadless[app],
        };
      }
    });

    return { appsToInstall, dependencyOptions };
  };

  const getData = () => {
    const dependenciesData: InstallerAppsData[] = [];
    Object.keys(appsMap).forEach((app) => {
      if (appsMap[app].data.toggle !== undefined || appsMap[app].parents.size) {
        dependenciesData.push({
          appDefId: app,
          ...appsMap[app].data,
        });
      }
    });
    return {
      dependenciesData,
      appToInstallName: appsMap[appToInstallDefinitionId].data.label!,
    };
  };

  return {
    getData,
    toggleOptionalDependency,
    getAppsToInstall,
  };
};

export { createDependenciesDriver };
